import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;
const BORDER_W = 2 * IN;

/**
 * Elevation schematic for a dorm area rug (rectangular or circular).
 *
 * Rugs are extremely thin, so the elevation view is essentially a thin
 * cross-section. The front view shows the full width with the border
 * detail; the side view shows the depth.
 */
export function DormRugSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <RugSideView
          span={depth}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SchematicShell>
    );
  }

  // Front view — thin cross-section showing border + surface layers
  const borderThick = height * 0.45;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Horizontal split between border layer (bottom) and surface (top) */}
      <Line
        x1={0}
        y1={height - borderThick}
        x2={width}
        y2={height - borderThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
      {/* Border inset lines — vertical marks showing where surface sits */}
      <Line
        x1={BORDER_W}
        y1={0}
        x2={BORDER_W}
        y2={height - borderThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
      <Line
        x1={width - BORDER_W}
        y1={0}
        x2={width - BORDER_W}
        y2={height - borderThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
      {/* Center accent line on the surface */}
      <Line
        x1={width * 0.35}
        y1={(height - borderThick) * 0.5}
        x2={width * 0.65}
        y2={(height - borderThick) * 0.5}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.3}
      />
    </SchematicShell>
  );
}

function RugSideView({ span, height, strokeColor, strokeWidth }) {
  const borderThick = height * 0.45;

  return (
    <g>
      {/* Layer split */}
      <Line
        x1={0}
        y1={height - borderThick}
        x2={span}
        y2={height - borderThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
      {/* Border inset */}
      <Line
        x1={BORDER_W}
        y1={0}
        x2={BORDER_W}
        y2={height - borderThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
      <Line
        x1={span - BORDER_W}
        y1={0}
        x2={span - BORDER_W}
        y2={height - borderThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
    </g>
  );
}

DormRugSchematic.supportsSideViews = true;
